import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { motion } from "motion/react";
import { Bug, Library, MonitorSmartphone } from "lucide-react";
import type { SlideProps } from "@/engine/types";
import { chapterMeta } from "@/engine/chapters";
import { GlassCard, Reveal, SlideShell } from "@/ui";

const [FROM, TO] = chapterMeta(4).accent;

/** Semibold-white emphasis (source deck's **bold**). */
function B({ children }: { children: ReactNode }) {
  return <span className="font-semibold text-white">{children}</span>;
}

/** Inline code token tinted with the chapter accent. */
function Mono({ children }: { children: ReactNode }) {
  return (
    <code
      className="rounded-md bg-white/10 px-2 py-0.5 font-mono text-[0.85em]"
      style={{ color: FROM }}
    >
      {children}
    </code>
  );
}

interface Stage {
  n: number;
  icon: typeof Bug;
  title: string;
  delay: number;
  lines: readonly ReactNode[];
}

const STAGES: readonly Stage[] = [
  {
    n: 1,
    icon: Bug,
    title: "Crawl",
    delay: 0.2,
    lines: [
      <>
        Bots can <B>reach</B> important URLs
      </>,
      <>
        <Mono>robots.txt</Mono> &amp; <B>status codes</B> behave as intended
      </>,
    ],
  },
  {
    n: 2,
    icon: Library,
    title: "Index",
    delay: 0.8,
    lines: [
      <>
        <B>Canonicals</B> &amp; <B>duplicates</B> under control
      </>,
      <>
        <Mono>hreflang</Mono> for international sites, where needed
      </>,
    ],
  },
  {
    n: 3,
    icon: MonitorSmartphone,
    title: "Render & experience",
    delay: 1.4,
    lines: [
      <>
        <B>Core Web Vitals</B>, mobile usability, HTTPS
      </>,
      <>
        Structured data (<Mono>JSON-LD</Mono>) when it <B>clarifies entities</B>
      </>,
    ],
  },
];

const NODES = [
  { label: "/", x: 100 },
  { label: "/categories", x: 340 },
  { label: "/product-page", x: 580 },
] as const;

/** Mini sitemap with a bot dot hopping across yoursite.example's URLs. */
function CrawlerVisual({ active }: { active: boolean }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!active) {
      setStep(0);
      return;
    }
    const id = setInterval(() => setStep((s) => (s + 1) % NODES.length), 1500);
    return () => clearInterval(id);
  }, [active]);

  const bot = NODES[step] ?? NODES[0];

  return (
    <div className="flex flex-col items-center gap-2">
      <svg viewBox="0 0 680 104" className="w-[680px] max-w-full" aria-hidden>
        {/* connecting rails */}
        <line
          x1={100}
          y1={38}
          x2={580}
          y2={38}
          stroke="rgba(255,255,255,0.15)"
          strokeWidth={2}
          strokeDasharray="6 8"
        />
        {NODES.map((node, i) => (
          <g key={node.label}>
            <motion.circle
              cx={node.x}
              cy={38}
              r={13}
              fill="rgba(255,255,255,0.05)"
              strokeWidth={2}
              animate={{
                stroke: step === i ? FROM : "rgba(255,255,255,0.25)",
                scale: step === i ? 1.15 : 1,
              }}
              style={{ transformOrigin: `${node.x}px 38px` }}
              transition={{ duration: 0.4 }}
            />
            <text
              x={node.x}
              y={92}
              textAnchor="middle"
              fontSize={20}
              className="fill-white/60 font-mono"
            >
              {node.label}
            </text>
          </g>
        ))}
        {/* Bot dot — animate a transform on <g>, not the cx attribute
            (motion serializes an uninitialised attribute as "undefined" for
            one frame during slide transitions) */}
        <motion.g
          initial={false}
          animate={{ x: bot.x }}
          transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
        >
          <circle
            cx={0}
            cy={38}
            r={7}
            fill={FROM}
            style={{ filter: `drop-shadow(0 0 8px ${FROM})` }}
          />
        </motion.g>
      </svg>
      <span className="text-xl text-white/50">
        Googlebot discovering URLs on yoursite.example
      </span>
    </div>
  );
}

export function TechnicalSeoSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={4} title="Technical SEO">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-12">
        {/* Pipeline */}
        <div className="flex w-full items-stretch gap-0">
          {STAGES.map(({ n, icon: Icon, title, delay, lines }, i) => (
            <div key={title} className="flex flex-1 items-center">
              {i > 0 && (
                <motion.div
                  className="mx-2 h-[3px] w-14 shrink-0 origin-left rounded-full"
                  style={{ backgroundImage: `linear-gradient(90deg, ${FROM}, ${TO})` }}
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={active ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
                  transition={{ delay: delay - 0.35, duration: 0.4, ease: "easeOut" }}
                />
              )}
              <Reveal active={active} delay={delay} className="min-w-0 flex-1 self-stretch">
                <GlassCard glow className="flex h-full flex-col gap-4 p-7">
                  <div className="flex items-center gap-4">
                    <span className="font-display text-4xl font-bold" style={{ color: FROM }}>
                      {n}
                    </span>
                    <Icon className="h-8 w-8 text-white/80" strokeWidth={2} />
                  </div>
                  <h3 className="font-display text-3xl font-semibold text-white">{title}</h3>
                  <ul className="flex flex-col gap-3">
                    {lines.map((line, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-3 text-2xl leading-snug text-white/80"
                      >
                        <span
                          className="mt-3 h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: FROM }}
                        />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </GlassCard>
              </Reveal>
            </div>
          ))}
        </div>

        {/* Crawler visual */}
        <Reveal active={active} delay={2.1} className="w-full">
          <GlassCard className="flex justify-center px-8 py-6">
            <CrawlerVisual active={active} />
          </GlassCard>
        </Reveal>
      </div>
    </SlideShell>
  );
}
